<template>
  <div :class="$style.config">
    <audio
      ref="audio"
      controls
      :class="$style.audio"
      @loadedmetadata="onReady"
      @timeupdate="onTimeUpdate"
      @ended="isPlaying = false"
      @pause="isPlaying = false"
      @play="isPlaying = true"
    >
      <source :src="src" type="audio/mpeg" />
      Your browser does not support the audio element.
    </audio>

    <div v-if="isReady" :class="$style.audioUi">
      <BaseButton
        presentation="strip"
        :class="$style.iconWrap"
        @click="onRequestTogglePlayPause"
      >
        <IconPlayRounded
          v-show="!isPlaying"
          :class="[$style.icon, $style.play]"
        />
        <IconPause v-show="isPlaying" :class="[$style.icon, $style.pause]" />
      </BaseButton>
      <div :class="$style.progressWrap">
        <div :class="$style.progressBar">
          <div :class="$style.track"></div>
          <div
            :class="$style.progress"
            :style="{
              width: computedProgressNum < 1 ? '1%' : computedProgress,
            }"
          />
        </div>
        <input
          v-model="currentTime"
          type="range"
          min="0"
          :max="duration"
          :class="$style.range"
          @input="onCurrentTimeInput"
          @keydown.space.stop.prevent="onRequestTogglePlayPause"
        />
        <div :class="$style.progressText">
          {{ computedCurrentTime }} / {{ computedDuration }}
        </div>
      </div>
      <div :class="$style.volume">
        <div :class="$style.progressWrap">
          <div
            :class="[
              $style.progressBar,
              isVolumeSeekMouseDown && $style.mousedown,
            ]"
          >
            <div :class="$style.track"></div>
            <div
              :class="$style.progress"
              :style="{
                width: currentVolume + '%',
              }"
            />
          </div>
          <input
            v-model="currentVolume"
            type="range"
            min="0"
            max="100"
            :class="$style.range"
            @input="onCurrentVolumeInput"
          />
        </div>
        <BaseButton presentation="strip" @click="onRequestToggleMute">
          <IconVolumeX
            v-show="isMuted || currentVolume < 1"
            :class="$style.icon"
          />
          <IconVolume0
            v-show="!isMuted && currentVolume <= 30 && currentVolume > 0"
            :class="$style.icon"
          />
          <IconVolume1
            v-show="!isMuted && currentVolume <= 70 && currentVolume > 30"
            :class="$style.icon"
          />
          <IconVolume2
            v-show="!isMuted && currentVolume > 70"
            :class="$style.icon"
          />
        </BaseButton>
      </div>
    </div>
    <div v-else :class="$style.loading"><LoaderDots /></div>
  </div>
</template>

<script setup lang="ts">
const audioEl = useTemplateRef('audio')

const emit = defineEmits(['ready'])

defineProps<{
  src: string
}>()

const isReady = ref(false)
const isPlaying = ref(false)
const isVolumeSeekMouseDown = ref(false)
const isMuted = ref(false)
const currentVolume = ref(100)
const currentTime = ref(0)
const duration = ref(0)

const computedProgress = computed(() => {
  const progressPercentage = (currentTime.value / duration.value) * 100
  return progressPercentage.toFixed(2) + '%'
})

const computedProgressNum = computed(() => {
  return parseFloat(computedProgress.value)
})

const computedDuration = computed(() => {
  return formatTime(duration.value, 'h:m:ss')
})

const computedCurrentTime = computed(() => {
  return formatTime(currentTime.value, 'h:m:ss')
})

const onRequestMuteAudio = () => {
  if (audioEl.value) {
    audioEl.value.muted = true
    currentVolume.value = 0
  }
}

const onRequestUnmuteAudio = () => {
  if (audioEl.value) {
    audioEl.value.muted = false
    currentVolume.value = audioEl.value.volume * 100
  }
}

const onRequestTogglePlayPause = () => {
  isPlaying.value = !isPlaying.value
}

const onRequestToggleMute = () => {
  isMuted.value = !isMuted.value
}

const onRequestAudioPlay = () => {
  audioEl.value?.play?.()
}

const onRequestAudioPause = () => {
  audioEl.value?.pause?.()
}

const onRequestAudioDuration = () => {
  return audioEl.value?.duration || 0
}

const onRequestAudioCurrentTime = () => {
  return audioEl.value?.currentTime || 0
}

const onReady = () => {
  setTimeout(() => {
    isReady.value = true
    emit('ready', audioEl.value)
  }, 1000)
  currentTime.value = onRequestAudioCurrentTime()
  duration.value = onRequestAudioDuration()
}

const onCurrentTimeInput = (e: Event) => {
  const el = e?.target as HTMLInputElement
  if (el && audioEl.value) {
    audioEl.value.currentTime = parseInt(el?.value || '')
    currentTime.value = parseInt(el?.value || '')
  }
}

const onCurrentVolumeInput = (e: Event) => {
  const el = e?.target as HTMLInputElement
  if (el?.value) {
    isMuted.value = false
  }
  if (audioEl.value) {
    audioEl.value.volume = parseInt(el?.value) / 100
    currentVolume.value = parseInt(el?.value || '')
  }
}

const onTimeUpdate = () => {
  currentTime.value = onRequestAudioCurrentTime()
}

watch(isPlaying, (newVal) => {
  if (newVal) {
    onRequestAudioPlay()
  } else {
    onRequestAudioPause()
  }
})

watch(isMuted, (newVal) => {
  if (newVal) {
    onRequestMuteAudio()
  } else {
    onRequestUnmuteAudio()
  }
})

onMounted(() => {
  onReady()
})

function formatTime(seconds: number, option = 'h:m:ss') {
  if (!seconds) return '0:00'

  const ss = `${Math.floor(seconds % 60)}`.padStart(2, '0')
  const mm = Math.floor(seconds / 60) % 60
  const hh = Math.floor(seconds / 60 / 60) % 24

  if (option === 'hh:mm:ss') {
    return `${hh ? `${hh}`.padStart(2, '0') + ':' : ''}${
      mm ? `${mm}`.padStart(2, '0') : '00'
    }:${ss}`
  }

  if (option === 'h:m:ss') {
    return `${hh ? hh + ':' : ''}${hh ? (mm ? `${mm}`.padStart(2, '0') : '00') : mm}:${ss}`
  }
}
</script>

<style lang="scss" module>
.config {
  --track-height: 5px;
  --seeker-size: 6px;
  --seeker-area-size: 18px;
  --icon-size: 18px;
  --track-background: var(--white);
  --progress-start-color: var(--purple-light);
  --progress-end-color: var(--purple);
  --volumn-height: 30px;
}

.audio {
  width: 100%;
  @include sr-only();
}

.audioUi {
  display: flex;
  align-items: center;
}

.iconWrap {
  border: 2px solid var(--white);
  border-radius: 50%;
  width: calc(var(--icon-size) * 2.8);
  height: calc(var(--icon-size) * 2.8);
  margin-right: 1em;
  display: grid;
  place-content: center;
}

.icon {
  color: var(--white) !important;
  width: var(--icon-size);
  height: var(--icon-size);

  &.play {
    transform: translateX(8%) scale(0.8);
  }
}

.progressWrap {
  flex: 1;
  position: relative;
}

.range {
  cursor: pointer;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: calc(100% + var(--seeker-size) * 2.5);
  margin-left: calc(
    0px - var(--seeker-size)
  ); // https://github.com/postcss/postcss-calc/issues/142
  opacity: 0 !important;
}

.progressText {
  user-select: none;
  pointer-events: none;
  color: var(--progress-start-color);
  font-size: 12px;
  position: absolute;
  top: 100%;
}

.progressBar {
  width: 100%;
  height: var(--seeker-area-size);
  position: relative;
}

.track {
  pointer-events: none;
  background: var(--track-background);
  position: absolute;
  transform: translateY(-50%);
  top: 50%;
  width: 100%;
  height: var(--track-height);
}

.track,
.progress {
  pointer-events: none;
  border-radius: calc(var(--track-height) / 2);
}

.progress {
  background: linear-gradient(
    to right,
    var(--progress-start-color) 0%,
    var(--progress-end-color) 100%
  );
  position: absolute;
  transform: translateY(-50%);
  top: 50%;
  height: var(--track-height);

  &::after {
    pointer-events: none;
    content: '';
    position: absolute;
    right: 0;
    top: 50%;
    width: calc(2 * var(--seeker-size));
    height: calc(2 * var(--seeker-size));
    background: var(--progress-end-color);
    border-radius: 50%;
    opacity: 1;
    transform: translateY(-50%);
    transition: opacity 1000ms ease 1000ms;
  }

  .progressWrap:hover &::after {
    opacity: 1;
    transition: opacity 500ms ease;
  }
}

.volume {
  border: 1px solid transparent;
  border-radius: 2em;
  transition:
    border 500ms ease 150ms,
    margin 400ms ease 150ms;

  display: flex;
  align-items: center;
  margin: 0 0 0 0.25em;
  padding: 0 0.75em;
  position: relative;

  height: var(--volumn-height);

  .progressWrap {
    width: 0;
    margin: 0;
    opacity: 0;
    transition:
      opacity 250ms ease 150ms,
      width 500ms ease 150ms,
      margin 400ms ease 150ms;
    display: flex;
  }

  &:focus-within,
  &:hover {
    border: 1px solid var(--progress-end-color);
    margin: 0 0 0 1em;
  }

  &:focus-within .progressWrap,
  &:hover .progressWrap {
    width: 4em;
    opacity: 1;
    margin: 0 0.75em 0 0.25em;
  }
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 3em;
}
</style>
